import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { getIsoDateString } from '../../../utils/dateutils';

import StepContainer from '../steps/StepContainer';
import styles from './idverificationstyles';
import {
  maxDateofBirth,
  validationSchema,
  earliestPassportExpiry,
  earliestDriverLicenceExpiry,
} from './validationSchema';
import { validationSchemaUploadId } from './validationSchemaUploadId';
import { digitalRegistrationStepTwoCompleteEvent } from '../../../utils/gtmUtils';
import { myNameIsNotHere } from './constants';
import startupConfig from '../../../config/startupConfig';
import IdVerificationFieldset from './IdVerificationFieldset';
import UploadId from './UploadId';
import { getAppInsights } from '../../../utils/telemetryUtils';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { portalTypes } from '../../../config';
import { PEP_STATUS } from '../../../constants';
import { checkSuspectedPEP } from '../../../utils/regoUtils';
import asyncSessionStorage from '../../../utils/asyncSessionStorage';
import { MARKETING_TAGS, ACCOUNT_TYPE } from '../../../constants';

let id = '';

class IdVerification extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      nameListClicked: false,
      isOnNameClickSSOSuccess: false,
      selectedContact: null,
      isPrefilledIdVerified: false,
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleNameListSelectionChange = this.handleNameListSelectionChange.bind(this);
    this.handleDocumentTypeSelectionChange = this.handleDocumentTypeSelectionChange.bind(this);
  }

  componentDidMount() {
    const isShadowRegisterOnStep2Enabled =
      startupConfig.get().featureConfiguration.isShadowRegisterOnStep2Enabled;

    if (isShadowRegisterOnStep2Enabled && !this.props.canVerifyIdUsingPrefilledDetails) {
      this.props.shadowRegisterAccount(this.props.abn).then((response) => {
        if (response) {
          const { isShadowRegistered, registrantMatchingContact } = response;
          const isSsoFlowEnabled = startupConfig.get().featureConfiguration.isSsoFlowEnabled;

          if (
            isSsoFlowEnabled &&
            this.props.registrationMode !== 'rm' &&
            isShadowRegistered &&
            registrantMatchingContact
          ) {
            const { firstName, lastName, dateOfBirth, isGcoAccessEnabled, isSpAccessEnabled } =
              registrantMatchingContact;
            this.props.ssoExistingContact(this.props.abn.id, {
              firstName,
              lastName,
              dateOfBirth,
              shadowRegisteredAccountId: this.props.shadowRegisteredAccountId,
              isRegistrantEligibleToRedirectToGco: isGcoAccessEnabled,
              isRegistrantEligibleToRedirectToSP: isSpAccessEnabled,
            });
          }
        }
      });
    }

    digitalRegistrationStepTwoCompleteEvent();
    this.props.resetVerificationResult();

    if (this.props.canVerifyIdUsingPrefilledDetails && this.props.abn && this.props.abn.id) {
      this.props.verifyIdPrefilled(this.props.abn.id).then((result) => {
        if (result && result.shouldInvokeRegisterNext) {
          const stepLink = result.links && result.links.find((link) => link.rel === 'Register');

          if (stepLink) {
            const callback = null;
            const errorCallback = () => {
              // reusing the flag hasErroredFromVerifyIdPrefilled for display of both prefill rego/ verify call errors
              this.props.updateHasErroredFromVerifyIdPrefilled(true);
            };
            this.setState({ isPrefilledIdVerified: true });
            this.props.register(stepLink.href, callback, errorCallback, {
              isPrefilledIdVerified: true,
            });
          }
        }
      });
    }
  }

  getMarketingTagsMapped() {
    const marketingTags = asyncSessionStorage.getItemSync(MARKETING_TAGS);
    const marketingTagsParsed = marketingTags && JSON.parse(marketingTags);
    return marketingTagsParsed;
  }

  getValidationSchema(isDisplayUploadID) {
    if (isDisplayUploadID) {
      return validationSchemaUploadId;
    }
    if (this.state.isOnNameClickSSOSuccess) {
      return yup.object().shape({});
    }
    return validationSchema;
  }

  handleNameListSelectionChange(setFieldValue, resetForm, isSsoFlowEnabled) {
    return (selected) => {
      resetForm();
      setFieldValue('selectedName', selected.id);
      setFieldValue('documentType', 'License');
      this.setState({ isOnNameClickSSOSuccess: false });
      if (selected.id) {
        const marketingTagsParsed = this.getMarketingTagsMapped();
        const isBrokerRegistering =
          marketingTagsParsed && marketingTagsParsed.account_type === ACCOUNT_TYPE.BROKER;
        id = selected.id.id;
        if (
          isSsoFlowEnabled &&
          this.props.registrationMode !== 'rm' &&
          !this.isPortalTypeSP() &&
          !isBrokerRegistering
        ) {
          this.processSsoFlow(selected);
        }
      }
      this.setState({ nameListClicked: true });
    };
  }

  handleDocumentTypeSelectionChange(setFieldValue) {
    return () => {
      setFieldValue('driversLicenceState', '');
      setFieldValue('driversLicenseNumber', '');
      setFieldValue('driversLicenceCardNumber', '');
      setFieldValue('documentExpirationDate', null);
      setFieldValue('firstName', '');
      setFieldValue('lastName', '');
      setFieldValue('passportNumber', '');
    };
  }

  processSsoFlow(selected) {
    this.props
      .verifySso({
        firstName: selected.id.firstName,
        lastName: selected.id.lastName,
        dateOfBirth: selected.id.dateOfBirth,
        shadowRegisteredAccountId: this.props.shadowRegisteredAccountId,
      })
      .then((response) => {
        if (response) {
          this.setState({ isOnNameClickSSOSuccess: true });
          this.setState({
            selectedContact: {
              firstName: selected.id.firstName,
              lastName: selected.id.lastName,
              dateOfBirth: selected.id.dateOfBirth,
            },
          });
        } else {
          this.setState({ isOnNameClickSSOSuccess: false });
        }
      });
  }

  trimInputValues(values) {
    values.firstName = values.firstName ? values.firstName.trim() : values.firstName;
    values.lastName = values.lastName ? values.lastName.trim() : values.lastName;
  }

  handleSubmit(
    nameNotListed,
    values,
    { setSubmitting },
    isDisplayUploadID,
    isUploadIdFlowEnabled,
    isAdminRegistration,
    isIdVerificationAttemptsMaxed,
  ) {
    this.trimInputValues(values);
    if (this.state.isOnNameClickSSOSuccess && this.state.selectedContact) {
      this.processNameSelectSso();
    } else if (isDisplayUploadID && !isAdminRegistration) {
      this.processIdUploadSuccess(
        values,
        isIdVerificationAttemptsMaxed,
        this.props.abn.id,
        this.props.shadowRegisteredAccountId,
        nameNotListed,
      );
    } else {
      this.props.updateIsVerifyIdPrefilledError(false);
      this.processRegistration(
        isUploadIdFlowEnabled,
        isAdminRegistration,
        nameNotListed,
        values,
        setSubmitting,
      );
    }
  }

  processRegistration(
    isUploadIdFlowEnabled,
    isAdminRegistration,
    nameNotListed,
    values,
    setSubmitting,
  ) {
    if (isUploadIdFlowEnabled && !isAdminRegistration) {
      this.props.register(
        `${this.props.abn.id}/Register`,
        () => {
          setSubmitting(false);
        },
        null,
        { nameNotListed, abn: this.props.abn.id, ...values },
      );
    } else {
      this.props.verifyId({ nameNotListed, ...values }).then((result) => {
        if (result && result.shouldInvokeRegisterNext) {
          const stepLink = result.links && result.links.find((link) => link.rel === 'Register');
          if (stepLink) {
            const callback = () => {
              setSubmitting(false);
            };
            const errorCallback = null;
            this.props.register(stepLink.href, callback, errorCallback);
          } else {
            setSubmitting(false);
          }
        } else {
          setSubmitting(false);
        }
      });
    }
  }

  async processNameSelectSso() {
    const { firstName, lastName, dateOfBirth } = this.state.selectedContact;
    this.props.updateCaseContact({ firstName, lastName, dateOfBirth }).then(() => {
      this.props
        .ssoExistingContact(this.props.abn.id, {
          firstName,
          lastName,
          dateOfBirth,
          shadowRegisteredAccountId: this.props.shadowRegisteredAccountId,
          isRegistrantEligibleToRedirectToGco: false,
        })
        .then(({ isOnNameClickSSOSuccess }) => {
          if (!isOnNameClickSSOSuccess) {
            this.setState({ isOnNameClickSSOSuccess: false });
          }
        });
    });
  }

  async processIdUploadSuccess(
    values,
    isIdVerificationAttemptsMaxed,
    abn,
    shadowRegisteredAccountId,
    nameNotListed,
  ) {
    await this.props.handleIdUploadSuccess(
      isIdVerificationAttemptsMaxed,
      nameNotListed,
      this.state.isPrefilledIdVerified,
      abn,
      values.selectIdType,
      this.props.userJourneyAccountType,
      values.isIdUploadFail,
    );
    if (this.state.isPrefilledIdVerified) {
      //user was invited and details were prefilled
      //proxy needs to fetch personal details from cache
      this.props.ssoContactPrefilledId(abn);
    } else if (!isIdVerificationAttemptsMaxed && !nameNotListed) {
      //thank you page shown for - max kyc attempt or name not here flow
      //else sso to portal
      var dateOfBirth = values.birthDate ? getIsoDateString(values.birthDate) : null;
      this.props.ssoExistingContact(abn, {
        firstName: values.firstName,
        lastName: values.lastName,
        dateOfBirth: dateOfBirth,
        shadowRegisteredAccountId: shadowRegisteredAccountId,
        isRegistrantEligibleToRedirectToGco: this.isPortalTypeCP(),
        isRegistrantEligibleToRedirectToSP: this.isPortalTypeSP(),
      });
    }
  }

  addRegisterDebugLog({ values, isDisplayUploadID }) {
    const appInsights = getAppInsights();
    const logObject = {
      isOnNameClickSSOSuccess: this.state.isOnNameClickSSOSuccess,
      isDisplayUploadID: isDisplayUploadID,
      firstName: values.firstName,
      lastName: values.lastName,
      dateOfBirth: values.dateOfBirth,
    };
    appInsights.trackTrace({
      message: `Register submit button clicked ${JSON.stringify(logObject)}`,
      severityLevel: SeverityLevel.Information,
    });
  }

  isPortalTypeSP = () => this.props.portalType === portalTypes.ShiftPayments;
  isPortalTypeCP = () => this.props.portalType === portalTypes.ClientPortal;

  render() {
    const {
      errorMessage,
      directorList,
      maximumDateofBirth,
      delay,
      canVerifyIdUsingPrefilledDetails,
      hasErroredFromVerifyIdPrefilled,
      isTrust,
      trusteeNamesList,
      isIdVerificationAttemptsMaxed,
      shadowRegisteredAccountId,
      pepStatus,
      isPotentialAMLIdentified,
      registrationMode,
      earliestAcceptablePassportExpiry,
      earliestAcceptableDriverLicenceExpiry,
      idVerificationResult,
      isVerifyIdPrefilledError,
    } = this.props;

    const isUploadIdFlowEnabled = startupConfig.get().featureConfiguration.isUploadIdFlowEnabled;
    const isDisplayUploadID =
      isIdVerificationAttemptsMaxed ||
      isPotentialAMLIdentified ||
      pepStatus === PEP_STATUS.SuspectedPEP;
    let selectedName = null;
    let firstName = '';
    let lastName = '';
    const isAdminRegistration = registrationMode && registrationMode === 'rm';

    if (canVerifyIdUsingPrefilledDetails && directorList && directorList.length > 0) {
      selectedName = directorList[0].id;
      firstName = directorList[0].id.firstName;
      lastName = directorList[0].id.lastName;
      id = directorList[0].id.id;
    }

    const schema = this.getValidationSchema(isDisplayUploadID);

    return (
      <Formik
        displayName="IdVerificationForm"
        validateOnBlur={false}
        initialValues={{
          selectedName,
          firstName,
          lastName,
          email: '',
          birthDate: null,
          documentType: '',
          driversLicenseNumber: '',
          passportNumber: '',
          driversLicenceCardNumber: '',
          documentExpirationDate: null,
          consent: false,
          homeAddress: null,
          driversLicenceDocumentFiles: [],
          passportDocumentFiles: [],
          medicareDocumentFiles: [],
          selectIdType: '',
          driversLicenceState: '',
        }}
        // enableReinitialize
        validationSchema={schema}
        onSubmit={(values, formikActions) => {
          this.addRegisterDebugLog({ values, isDisplayUploadID });
          const isNameNotListed = values.selectedName === false;
          this.handleSubmit(
            isNameNotListed,
            values,
            formikActions,
            isDisplayUploadID,
            isUploadIdFlowEnabled,
            isAdminRegistration,
            isIdVerificationAttemptsMaxed,
          );
        }}
        render={({
          submitForm,
          isSubmitting,
          values,
          dirty,
          errors,
          setFieldValue,
          resetForm,
          touched,
          setFieldError,
        }) => {
          const isTrustWithIndividualTrustee =
            isTrust &&
            trusteeNamesList &&
            trusteeNamesList.length > 0 &&
            trusteeNamesList[0].id.firstName &&
            trusteeNamesList[0].id.lastName;

          const isMyNameNotHere = isTrustWithIndividualTrustee
            ? false
            : values.selectedName === false;
          const isNameListVisible = isTrustWithIndividualTrustee
            ? true
            : directorList && directorList.length > 0;
          const isAMLPEPPassed = !checkSuspectedPEP(pepStatus) && !isPotentialAMLIdentified;

          const isSubmitDisabled =
            isSubmitting ||
            !dirty ||
            (isDisplayUploadID &&
              values.driversLicenceDocumentFiles.length === 0 &&
              values.passportDocumentFiles.length === 0);

          const isFormInputAndSubmitVisible =
            !isNameListVisible ||
            (this.state.nameListClicked && !this.state.isOnNameClickSSOSuccess) ||
            hasErroredFromVerifyIdPrefilled ||
            isDisplayUploadID;

          const isSubmitVisible =
            !isNameListVisible ||
            this.state.nameListClicked ||
            hasErroredFromVerifyIdPrefilled ||
            isDisplayUploadID;

          values.id = isNameListVisible && !isMyNameNotHere ? id : null;

          const submitText = isDisplayUploadID ? 'Continue' : 'Register';

          return (
            <StepContainer
              title="Your personal details"
              isSubmitVisible={isSubmitVisible}
              isSubmitDisabled={this.state.isOnNameClickSSOSuccess ? false : isSubmitDisabled}
              submitLabel={submitText}
              submitButtonAdditionalCssClassName="ga-reg-details"
              onSubmit={submitForm}
            >
              <Form autoComplete="off" data-testid="idVerificationForm">
                {!isDisplayUploadID && (
                  <IdVerificationFieldset
                    values={values}
                    isSubmitting={isSubmitting}
                    errorMessage={errorMessage}
                    isNameListVisible={isNameListVisible}
                    isTrustWithIndividualTrustee={isTrustWithIndividualTrustee}
                    trusteeNamesList={trusteeNamesList}
                    directorList={directorList}
                    setFieldValue={setFieldValue}
                    setFieldError={setFieldError}
                    resetForm={resetForm}
                    isMyNameNotHere={isMyNameNotHere}
                    myNameIsNotHere={myNameIsNotHere}
                    isFormInputAndSubmitVisible={isFormInputAndSubmitVisible}
                    maximumDateofBirth={maximumDateofBirth}
                    handleNameListSelectionChange={this.handleNameListSelectionChange}
                    handleDocumentTypeSelectionChange={this.handleDocumentTypeSelectionChange}
                    delay={delay}
                    earliestAcceptablePassportExpiry={earliestAcceptablePassportExpiry}
                    earliestAcceptableDriverLicenceExpiry={earliestAcceptableDriverLicenceExpiry}
                    isVerifyIdPrefilledError={isVerifyIdPrefilledError}
                    idVerificationResult={idVerificationResult}
                  />
                )}
                {isDisplayUploadID && (
                  <UploadId
                    values={values}
                    touched={touched}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    shadowRegisteredAccountId={shadowRegisteredAccountId}
                    userJourneyAccountType={this.props.userJourneyAccountType}
                    isAMLPEPPassed={isAMLPEPPassed}
                    errorMessage={errorMessage}
                  />
                )}
              </Form>
            </StepContainer>
          );
        }}
      />
    );
  }
}

IdVerification.propTypes = {
  shadowRegisterAccount: PropTypes.func.isRequired,
  ssoExistingContact: PropTypes.func.isRequired,
  verifySso: PropTypes.func.isRequired,
  resetVerificationResult: PropTypes.func.isRequired,
  handleIdUploadSuccess: PropTypes.func.isRequired,
  idVerificationResult: PropTypes.object,
  verifyId: PropTypes.func.isRequired,
  verifyIdPrefilled: PropTypes.func.isRequired,
  updateHasErroredFromVerifyIdPrefilled: PropTypes.func.isRequired,
  updateIsVerifyIdPrefilledError: PropTypes.func.isRequired,
  register: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  maximumDateofBirth: PropTypes.instanceOf(Date),
  directorList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.any,
      text: PropTypes.string.isRequired,
    }),
  ),
  canVerifyIdUsingPrefilledDetails: PropTypes.bool,
  hasErroredFromVerifyIdPrefilled: PropTypes.bool,
  isVerifyIdPrefilledError: PropTypes.bool,
  abn: PropTypes.shape({
    id: PropTypes.string,
  }),
  delay: PropTypes.number,
  pepStatus: PropTypes.string,
  isTrust: PropTypes.bool,
  isPotentialAMLIdentified: PropTypes.bool,
  trusteeNamesList: PropTypes.array,
  registrationMode: PropTypes.string,
  isIdVerificationAttemptsMaxed: PropTypes.bool,
  shadowRegisteredAccountId: PropTypes.string,
  earliestAcceptablePassportExpiry: PropTypes.instanceOf(Date),
  earliestAcceptableDriverLicenceExpiry: PropTypes.instanceOf(Date),
};

IdVerification.defaultProps = {
  idVerificationResult: {
    isVerified: false,
    isBirthDateMatched: false,
    isNameMatched: false,
    isTravelDocNoMatched: false,
    isGenderMatched: false,
    isExpiryDateMatched: false,
    isLicenseNumberMatched: false,
    isStateMatched: false,
    noOfAttempts: 0,
    isCardNumberMatched: false,
    verificationResultCode: '',
  },
  errorMessage: null,
  maximumDateofBirth: maxDateofBirth,
  directorList: null,
  canVerifyIdUsingPrefilledDetails: false,
  hasErroredFromVerifyIdPrefilled: false,
  isVerifyIdPrefilledError: false,
  abn: null,
  delay: 800,
  isTrust: false,
  trusteeNamesList: null,
  registrationMode: null,
  isIdVerificationAttemptsMaxed: false,
  shadowRegisteredAccountId: null,
  pepStatus: '',
  isPotentialAMLIdentified: false,
  earliestAcceptablePassportExpiry: earliestPassportExpiry,
  earliestAcceptableDriverLicenceExpiry: earliestDriverLicenceExpiry,
};

export default withStyles(styles)(IdVerification);
